export const DOCUMENT_SERVER_STATUS = {
  pending: 'pending',
  auto_reviewed: 'auto_reviewed',
  reviewed: 'reviewed',
  skipped: 'skipped',
  rejected: 'rejected',
  processing: 'processing',
} as const

export const DATA_EXTRACTION_FIELDS_CONFIG = {
  patient_name: {
    label: 'Patient Full Name',
    type: 'textfield',
  },
  patient_first_name: {
    label: 'Patient First Name',
    type: 'textfield',
  },
  patient_last_name: {
    label: 'Patient Last Name',
    type: 'textfield',
  },
  patient_dob: {
    label: 'Patient Date of Birth',
    type: 'date-input',
  },
  patient_id: {
    label: 'Patient ID',
    type: 'textfield',
    tooltip:
      'The patient ID is the unique identification that enables us to differentiate the member from the patient. For example, a patient ID will support a spouse or child who shares the same insurance as the subscribing member.',
  },
  member_name: {
    label: 'Member Full Name',
    type: 'textfield',
  },
  member_dob: {
    label: 'Member Date of Birth',
    type: 'date-input',
  },
  case_number: {
    label: 'Case Number',
    type: 'textfield',
    tooltip:
      'AKA: Case ID, LIMS Case ID\nA unique identifier assigned by Natera Sales Order System (SOS) to a particular sample that is collected from a patient. This number becomes the baseline for the LIMS Case ID, Statement ID to track the sample through processing, reporting, and billing.',
  },
  // This is `service_date_actual` in the spreadsheet
  service_date_from: {
    label: 'Date of Service',
    type: 'date-input',
  },
  insurance_company: {
    label: 'Payor Name Extracted',
    type: 'textfield',
  },
  member_id: {
    label: 'Member ID (AKA: Policy Number)',
    shortLabel: 'Member ID',
    type: 'textfield',
    tooltip:
      "AKA: Subscriber ID, Subscriber Number, Member Number, Policy Number\nA unique identifier for the patient's insurance policy provided by the payor. This can be numbers only or an alphanumeric combination.",
  },
  service_provider_name: {
    label: 'Provider Name',
    type: 'textfield',
  },
  service_provider_number: {
    label: 'Provider Number',
    type: 'textfield',
    tooltip: 'The NPI Registry number assigned to a provider (doctor, nurse, specialist).',
  },
  contact_phone: {
    label: 'Payor Phone',
    type: 'textfield',
  },
  contact_fax: {
    label: 'Payor Fax',
    type: 'textfield',
  },
  contact_email: {
    label: 'Payor Email',
    type: 'textfield',
  },
  contact_website: {
    label: 'Website',
    type: 'textfield',
  },
  contact_mail_address: {
    label: 'Primary Mailing Address',
    type: 'textfield',
  },
  contact_courier_mail_address: {
    label: 'Courier Mailing Address',
    type: 'textfield',
  },
  total_amount: {
    label: 'Total Billed',
    type: 'textfield',
    materialLeadIcon: 'attach_money',
  },
  barcode: {
    label: 'Barcode',
    type: 'textfield',
    tooltip:
      'The barcode is a unique identifier provided by the insurance company / payor on their correspondence. It is a way for them to track the letters they send with our responses.',
  },
  authorization_code: {
    label: 'Authorization Code',
    type: 'textfield',
    tooltip:
      'Authorization number or reference/tracking number associated with prior, concurrent, retrospective authorizations, claims processing, approvals, denials, or appeals. (Exclude general "case numbers" unless explicitly used as authorization references.) Similar keywords include: authorization number, reference number, tracking number, claim authorization number, DCN number',
  },
  appeal_id: {
    label: 'Appeal ID',
    type: 'textfield',
    tooltip:
      'The payor-assigned identifier for an appeal or reconsideration case. Keywords: appeal ID, appeal number, appeal case number, reconsideration number, grievance number.',
  },
  reference_number: {
    label: 'Reference Number',
    type: 'textfield',
    tooltip:
      'A general reference or tracking number the payor assigns to the letter, document, or correspondence (not specific to an authorization or appeal). Keywords: reference number, reference #, ref number, tracking number, document reference, correspondence number.',
  },
  patient_account_number: {
    label: 'Visit ID (AKA: Patient Account Number)',
    shortLabel: 'Visit ID',
    type: 'textfield',
    tooltip:
      "AKA: Visit ID, Patient Account Number, Patient ID, AMD Claim ID + submission order\nA unique identifier that represents the claim and how many times we've submitted it created by Natera in AMD. The format is the AMD Visit ID + an alpha character that represents the submission order / instance; A = 1st submission). ie: 12345678A\n\nPatient number will never be on prior authorization docs unless talking about claim & appeal related PA docs. PA submission comes before AMD visit creation & claim submission.",
  },
  decision: {
    label: 'Decision',
    type: 'textfield',
  },
  claim_number: {
    label: 'Claim Number',
    type: 'textfield',
    tooltip:
      'AKA: Claim Number\nThe number assigned to a claim by the payor, this is not generated by Natera.',
  },
  natera_claim_id: {
    label: 'Natera Claim ID',
    type: 'textfield',
  },
  carrier_claim_id: {
    label: 'Carrier Claim ID',
    type: 'textfield',
  },
  claim_filing_deadline: {
    label: 'Deadline to File Claim',
    type: 'date-input',
  },
  claim_appeal_deadline: {
    label: 'Deadline to File Appeal',
    type: 'date-input',
  },
  appeal_date_received: {
    label: 'Appeal Date Received',
    type: 'date-input',
    tooltip:
      'The date the payor received the claim appeal, dispute, reconsideration, or grievance. On Claim Appeal Acknowledgement letters this marks the start of the appeal review timeframe.',
  },
  notification_date_provided: {
    label: 'Notification Date Provided',
    type: 'date-input',
    tooltip:
      'The date the payor issued this acknowledgement/notification to the provider, marking the end of the acknowledgement timeframe.',
  },
  test_name: {
    label: 'Test Name',
    type: 'textfield',
  },
  claim_filing_date: {
    label: 'Claim Filed',
    type: 'date-input',
  },
  cpt_code: {
    label: 'CPT Code',
    type: 'textfield',
    tooltip:
      'CPT (Current Procedural Terminology) codes are standardized, five-digit numeric codes used by healthcare providers to document and report the medical, surgical, and diagnostic procedures and services they provide to patients. They are essential for medical billing and insurance reimbursement.',
  },
  reason_for_denial: {
    label: 'Reason for Decision',
    type: 'textfield',
  },
  deadline_to_resolve: {
    label: 'Deadline to Resolve',
    type: 'date-input',
  },
  carc_codes: {
    label: 'Reason code (CARC)',
    shortLabel: 'CARC',
    type: 'carc',
    placeholder: 'Select one or more CARCs',
    hint: 'CARC - Claim Adjustment Reason Code',
  },
  rarc_codes: {
    label: 'Additional details (RARC)',
    shortLabel: 'RARC',
    type: 'rarc',
    placeholder: 'Select one or more RARCs',
    hint: 'RARC - Remittance Advice Remark Code',
  },
  document_date: {
    label: 'Document Date',
    type: 'date-input',
  },
  auth_date_range_from: {
    label: 'Effective Date',
    type: 'date-input',
    tooltip: 'Start date of the prior authorization validity period.',
  },
  auth_date_range_to: {
    label: 'Expiration Date',
    type: 'date-input',
    tooltip: 'End date of the prior authorization validity period.',
  },
  natera_tags: {
    label: 'Natera Tags',
    type: 'natera-tags',
    placeholder: 'Select one or more tags',
  },
  payor_id: {
    label: 'Payor Name',
    type: 'custom',
  },
  natera_tags_notes: {
    label: 'Natera Tags Notes',
    type: 'mandatory-comment',
  },
  peer_to_peer_indicator: {
    label: 'Peer-to-Peer',
    type: 'select',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    defaultValue: 'false',
  },
} as const

export type DataExtractionFieldName = keyof typeof DATA_EXTRACTION_FIELDS_CONFIG
type DataExtractionFieldConfig = (typeof DATA_EXTRACTION_FIELDS_CONFIG)[DataExtractionFieldName]
export type DataExtractionFieldType = DataExtractionFieldConfig['type']

export function getMaterialLeadIcon(entry: DataExtractionFieldConfig): string | undefined {
  if ('materialLeadIcon' in entry) {
    return entry.materialLeadIcon
  }
  return undefined
}

export const ASSET_ID_QUERY_PARAM = 'assetId'
export const PHASE_QUERY_PARAM = 'phase'
export const STATUS_QUERY_PARAM = 'status'
export const ALL_QUERY_PARAM_VALUE = 'all'
export const CLAIM_PHASES = ['claim', 'claim_appeal']
export const PRIOR_AUTH_PHASES = ['prior_auth', 'prior_auth_appeal']
